import React, { useEffect, useState, useCallback } from 'react';
import { Plug, Plus, Trash2, Play, Save, RefreshCw, Loader, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import PageHeader from '../../components/PageHeader';
import { cn } from '../../utils/cn';
import { PAGE, PageLoader } from '../../components/crm/ui';

const BLANK_PARAM = { name: '', type: 'string', in: 'path', required: true, description: '' };
const BLANK_FIELD = { label: '', path: '' };

const BLANK_TOOL = {
  name: '', description: '', method: 'GET', urlTemplate: '',
  authType: 'none', authKeyName: '', authValue: '', oauthTokenUrl: '', oauthScope: '',
  params: [{ ...BLANK_PARAM }],
  responseFields: [{ ...BLANK_FIELD }],
  resultMode: 'object', arrayPath: '',
  requiresVerifiedIdentity: false, verifiedIdentifierParamName: '',
  rateLimitPerCustomer: { enabled: false, maxCalls: 10, windowMinutes: 60 },
  isActive: true,
};

export default function SupportApiPlaygroundPage() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | {...} (existing)
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [testArgs, setTestArgs] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [copiedName, setCopiedName] = useState(null);
  const [importing, setImporting] = useState(false); // import dialog open
  const [importText, setImportText] = useState('');
  const [importBase, setImportBase] = useState('');
  const [importDrafts, setImportDrafts] = useState(null); // null = not read yet, else drafts with a `keep` flag
  const [importBusy, setImportBusy] = useState(false);
  const [callLogs, setCallLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/support/api-tools');
      setTools(res.data?.data || []);
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to load API tools.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addParam = () => setEditing(p => ({ ...p, params: [...p.params, { ...BLANK_PARAM }] }));
  const patchParam = (idx, field, value) => setEditing(p => ({
    ...p, params: p.params.map((prm, i) => i === idx ? { ...prm, [field]: value } : prm),
  }));
  const removeParam = (idx) => setEditing(p => ({ ...p, params: p.params.filter((_, i) => i !== idx) }));

  const addField = () => setEditing(p => ({ ...p, responseFields: [...p.responseFields, { ...BLANK_FIELD }] }));
  const patchField = (idx, field, value) => setEditing(p => ({
    ...p, responseFields: p.responseFields.map((f, i) => i === idx ? { ...f, [field]: value } : f),
  }));
  const removeField = (idx) => setEditing(p => ({ ...p, responseFields: p.responseFields.filter((_, i) => i !== idx) }));

  const saveTool = async () => {
    if (!editing.name?.trim() || !editing.description?.trim() || !editing.urlTemplate?.trim()) {
      return alertService.error('Missing Info', 'Name, description, and URL are required.');
    }
    setSaving(true);
    try {
      if (editing._id) {
        const res = await api.put(`/support/api-tools/${editing._id}`, editing);
        setTools(prev => prev.map(t => t._id === editing._id ? res.data.data : t));
      } else {
        const res = await api.post('/support/api-tools', editing);
        setTools(prev => [res.data.data, ...prev]);
      }
      setEditing(null);
      alertService.success('Saved', 'API tool saved — it\'s now available for the agent to call if active.');
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to save tool.');
    } finally {
      setSaving(false);
    }
  };

  const deleteTool = async (id) => {
    try {
      await api.delete(`/support/api-tools/${id}`);
      setTools(prev => prev.filter(t => t._id !== id));
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to delete tool.');
    }
  };

  const toggleActive = async (tool) => {
    try {
      const res = await api.put(`/support/api-tools/${tool._id}`, { isActive: !tool.isActive });
      setTools(prev => prev.map(t => t._id === tool._id ? res.data.data : t));
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to update tool.');
    }
  };

  const openTestPanel = (tool) => {
    if (expandedId === tool._id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(tool._id);
    setTestResult(null);
    const initialArgs = {};
    tool.params.forEach(p => { initialArgs[p.name] = ''; });
    setTestArgs(initialArgs);
    loadCallLogs(tool._id);
  };

  // The track record — every real invocation of this tool, from any channel
  // (bot conversations, the capability registry, identity verification,
  // external module views), not just manual "Run" tests above.
  const loadCallLogs = async (toolId) => {
    setLoadingLogs(true);
    try {
      const res = await api.get(`/support/api-tools/${toolId}/logs`);
      setCallLogs(res.data.data || []);
    } catch {
      setCallLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const STATUS_LABELS = {
    success: { text: 'Success', className: 'text-[#2E844A] bg-[#E1F2E8]' },
    http_error: { text: 'API Error', className: 'text-[#BA0517] bg-[#FDE7E9]' },
    blocked_credits: { text: 'Out of Credits', className: 'text-[#A67A00] bg-[#FFF3D6]' },
    blocked_identity: { text: 'Needs Identity', className: 'text-[#A67A00] bg-[#FFF3D6]' },
    blocked_rate_limit: { text: 'Rate Limited', className: 'text-[#A67A00] bg-[#FFF3D6]' },
    config_error: { text: 'Config Error', className: 'text-[#BA0517] bg-[#FDE7E9]' },
  };

  const runTest = async (tool) => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(`/support/api-tools/${tool._id}/test`, { args: testArgs });
      setTestResult(res.data.data);
    } catch (err) {
      setTestResult({ error: err.response?.data?.error || 'Test call failed.' });
    } finally {
      setTesting(false);
    }
  };

  const copyName = (name) => {
    navigator.clipboard.writeText(name);
    setCopiedName(name);
    setTimeout(() => setCopiedName(null), 1500);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className={PAGE}>
      <PageHeader
        title="API Playground"
        subtitle="Build, test, and enable any REST API as a tool your support agent can call — order lookups, product catalogs, shipping rates, anything."
        icon={Plug}
      />

      {!editing && (
        <div className="flex gap-2">
          <button onClick={() => setEditing({ ...BLANK_TOOL })} className="flex items-center gap-2 bg-[#0176D3] hover:bg-[#014486] text-white px-4 h-9 rounded font-medium text-[13px]">
            <Plus size={16} /> New API Tool
          </button>
          <button onClick={() => { setImporting(true); setImportDrafts(null); }} className="flex items-center gap-2 border border-[#0176D3] text-[#0176D3] hover:bg-[#EAF4FE] px-4 h-9 rounded font-medium text-[13px]">
            Import from API spec
          </button>
        </div>
      )}

      {importing && !editing && (
        <div className="sf-card p-6 space-y-3">
          <h2 className="sf-card-title">Import from an API spec</h2>
          <p className="text-[12px] text-[#706E6B]">Paste an OpenAPI 3, Swagger 2 or Postman file (JSON or YAML). You review the tools first; they are added switched off, with a risk level suggested from what each one does.</p>
          {!importDrafts ? (
            <>
              <textarea value={importText} onChange={e => setImportText(e.target.value)} rows={10} placeholder="openapi: 3.0.0&#10;paths: ..." className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[12px] font-mono" />
              <input type="text" value={importBase} onChange={e => setImportBase(e.target.value)} placeholder="Base URL (optional, overrides the spec's server)" className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
              <div className="flex gap-2">
                <button disabled={importBusy || !importText.trim()} onClick={async () => {
                  setImportBusy(true);
                  try {
                    const res = await api.post('/support/api-tools/import/preview', { spec: importText, baseUrl: importBase || undefined });
                    setImportDrafts((res.data.data || []).map(d => ({ ...d, keep: d.urlOk !== false })));
                  } catch (err) { alertService.error('Could not read the spec', err?.response?.data?.error || 'Check the file and try again.'); } finally { setImportBusy(false); }
                }} className="bg-[#0176D3] disabled:opacity-50 text-white px-4 h-9 rounded text-[13px] font-semibold">{importBusy ? 'Reading…' : 'Read spec'}</button>
                <button onClick={() => setImporting(false)} className="px-4 h-9 rounded text-[13px] text-[#706E6B]">Cancel</button>
              </div>
            </>
          ) : (
            <>
              <div className="max-h-72 overflow-y-auto border border-[#F3F2F2] rounded divide-y divide-[#F3F2F2]">
                {importDrafts.map((d, i) => (
                  <label key={i} className="flex items-center gap-3 px-3 py-2 text-[12px]">
                    <input type="checkbox" checked={d.keep} disabled={d.urlOk === false} onChange={e => setImportDrafts(list => list.map((x, j) => j === i ? { ...x, keep: e.target.checked } : x))} />
                    <span className="font-mono font-semibold">{d.name}</span>
                    <span className="text-[#706E6B]">{d.method}</span>
                    <span className={d.riskTier === 'read' ? 'text-[#036B3A]' : 'text-[#BA0517]'}>{d.riskTier}</span>
                    <span className="text-[#706E6B] truncate">{d.description}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <button disabled={importBusy || !importDrafts.some(d => d.keep)} onClick={async () => {
                  setImportBusy(true);
                  try {
                    const res = await api.post('/support/api-tools/import/commit', { tools: importDrafts.filter(d => d.keep) });
                    const { created = [], skipped = [] } = res.data.data || {};
                    alertService.toast('success', `${created.length} tool(s) added (switched off)${skipped.length ? `, ${skipped.length} skipped` : ''}`);
                    setImporting(false); setImportText('');
                    await load();
                  } catch (err) { alertService.error('Import failed', err?.response?.data?.error || 'Could not add the tools.'); } finally { setImportBusy(false); }
                }} className="bg-[#0176D3] disabled:opacity-50 text-white px-4 h-9 rounded text-[13px] font-semibold">{importBusy ? 'Adding…' : 'Add selected tools'}</button>
                <button onClick={() => setImportDrafts(null)} className="px-4 h-9 rounded text-[13px] text-[#706E6B]">Back</button>
              </div>
            </>
          )}
        </div>
      )}

      {editing && (
        <div className="sf-card p-6 space-y-4">
          <h2 className="sf-card-title">{editing._id ? 'Edit API Tool' : 'New API Tool'}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">
                Tool Name <span className="text-[#706E6B] font-normal">(what the agent calls it — e.g. "check_shipping_rate")</span>
              </label>
              <input type="text" value={editing.name} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} placeholder="get_order_tracking" className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Method</label>
              <select value={editing.method} onChange={e => setEditing(p => ({ ...p, method: e.target.value }))} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                <option value="GET">GET</option>
                <option value="POST">POST</option>
              </select>
            </div>
          </div>

          <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Risk level <span className="normal-case font-normal">(approval needed above the agent's auto-approve level)</span></label>
              <select value={editing.riskTier || ''} onChange={e => setEditing(p => ({ ...p, riskTier: e.target.value || undefined }))} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                <option value="">Automatic (GET = read, POST = write)</option>
                <option value="read">Read: only looks things up</option>
                <option value="write">Write: creates or changes something</option>
                <option value="money">Money: payments, refunds, charges</option>
                <option value="destructive">Destructive: cancels or deletes</option>
              </select>
            </div>


          <div>
            <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">
              Description <span className="text-[#706E6B] font-normal">(tells the agent what this does and when to use it)</span>
            </label>
            <textarea value={editing.description} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} placeholder="Look up real-time shipping tracking info for an order by its tracking number." className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] h-16" />
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">
              URL Template <span className="text-[#706E6B] font-normal">(use {'{paramName}'} for path parameters)</span>
            </label>
            <input type="text" value={editing.urlTemplate} onChange={e => setEditing(p => ({ ...p, urlTemplate: e.target.value }))} placeholder="https://api.mystore.com/tracking/{trackingNumber}" className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">
                Result Mode <span className="text-[#706E6B] font-normal">(Single Object for lookups, List for a collection of records)</span>
              </label>
              <select value={editing.resultMode} onChange={e => setEditing(p => ({ ...p, resultMode: e.target.value }))} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                <option value="object">Single Object</option>
                <option value="list">List of Records</option>
              </select>
            </div>
            {editing.resultMode === 'list' && (
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">
                  Array Path <span className="text-[#706E6B] font-normal">(blank = response itself is the array)</span>
                </label>
                <input type="text" value={editing.arrayPath} onChange={e => setEditing(p => ({ ...p, arrayPath: e.target.value }))} placeholder="data.records" className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Auth Type</label>
              <select value={editing.authType} onChange={e => setEditing(p => ({ ...p, authType: e.target.value }))} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                <option value="none">None</option>
                <option value="bearer">Bearer token</option>
                <option value="header">Custom header</option>
                <option value="query">Query param</option>
                <option value="oauth2_client_credentials">OAuth2 (client credentials)</option>
              </select>
            </div>
            {editing.authType === 'oauth2_client_credentials' && (
              <div className="sm:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Token URL</label>
                  <input type="text" value={editing.oauthTokenUrl || ''} onChange={e => setEditing(p => ({ ...p, oauthTokenUrl: e.target.value }))} placeholder="https://auth.example.com/oauth/token" className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Scope (optional)</label>
                  <input type="text" value={editing.oauthScope || ''} onChange={e => setEditing(p => ({ ...p, oauthScope: e.target.value }))} placeholder="orders.read" className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
                </div>
              </div>
            )}
            {editing.authType !== 'none' && editing.authType !== 'bearer' && (
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">{editing.authType === 'header' ? 'Header Name' : editing.authType === 'oauth2_client_credentials' ? 'Client ID' : 'Query Param'}</label>
                <input type="text" value={editing.authKeyName} onChange={e => setEditing(p => ({ ...p, authKeyName: e.target.value }))} placeholder="X-API-Key" className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
              </div>
            )}
            {editing.authType !== 'none' && (
              <div className={editing.authType === 'bearer' ? 'col-span-2' : ''}>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">
                  {editing.authType === 'oauth2_client_credentials' ? (editing._id ? 'New Client Secret (blank = keep existing)' : 'Client Secret') : (editing._id ? 'New Secret (blank = keep existing)' : 'Secret')}
                </label>
                <input type="password" value={editing.authValue} onChange={e => setEditing(p => ({ ...p, authValue: e.target.value }))} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-[#D8DDE6]">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[12px] font-semibold text-[#706E6B] ">Parameters (what the agent must/can provide)</label>
              <button type="button" onClick={addParam} className="flex items-center gap-1 px-2 py-1 bg-[#EAF4FE] text-[#0176D3] rounded text-[13px] font-semibold"><Plus size={12} /> Add Param</button>
            </div>
            <div className="space-y-2">
              {editing.params.map((p, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <input type="text" value={p.name} onChange={e => patchParam(idx, 'name', e.target.value)} placeholder="orderId" className="col-span-3 px-2 py-1.5 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
                  <select value={p.type} onChange={e => patchParam(idx, 'type', e.target.value)} className="col-span-2 px-2 py-1.5 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                    <option value="string">string</option>
                    <option value="number">number</option>
                    <option value="boolean">boolean</option>
                  </select>
                  <select value={p.in} onChange={e => patchParam(idx, 'in', e.target.value)} className="col-span-2 px-2 py-1.5 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                    <option value="path">in path</option>
                    <option value="query">in query</option>
                    <option value="body">in body</option>
                  </select>
                  <input type="text" value={p.description} onChange={e => patchParam(idx, 'description', e.target.value)} placeholder="description for the agent" className="col-span-3 px-2 py-1.5 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
                  <label className="col-span-1 flex items-center gap-1 text-[12px]">
                    <input type="checkbox" checked={p.required} onChange={e => patchParam(idx, 'required', e.target.checked)} /> req
                  </label>
                  <button type="button" onClick={() => removeParam(idx)} className="col-span-1 p-1 text-[#BA0517] hover:bg-[#FDE7E9] rounded"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-[#D8DDE6]">
            <label className="flex items-center gap-2 text-[12px] font-semibold text-[#706E6B] mb-2">
              <input type="checkbox" checked={editing.requiresVerifiedIdentity} onChange={e => setEditing(p => ({ ...p, requiresVerifiedIdentity: e.target.checked }))} />
              Requires Verified Identity
            </label>
            <p className="text-[11px] text-[#706E6B] mb-2">
              When checked, WhatsApp callers must complete identity verification before this tool can run — see Support Widget settings to configure how verification works.
            </p>
            {editing.requiresVerifiedIdentity && (
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Auto-fill from verified identity</label>
                <select value={editing.verifiedIdentifierParamName} onChange={e => setEditing(p => ({ ...p, verifiedIdentifierParamName: e.target.value }))} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                  <option value="">Select a param…</option>
                  {editing.params.filter(p => p.name.trim()).map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                </select>
                <p className="text-[11px] text-[#706E6B] mt-1">This param's value is always overwritten server-side with the verified identifier — the model/user can never override it.</p>
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-[#D8DDE6]">
            <label className="flex items-center gap-2 text-[12px] font-semibold text-[#706E6B] mb-2">
              <input type="checkbox" checked={editing.rateLimitPerCustomer.enabled} onChange={e => setEditing(p => ({ ...p, rateLimitPerCustomer: { ...p.rateLimitPerCustomer, enabled: e.target.checked } }))} />
              Rate Limit Per Customer
            </label>
            <p className="text-[11px] text-[#706E6B] mb-2">
              Throttles how often ONE customer (by their conversation) can call this tool — independent of your overall API credit balance. Useful for sensitive or costly tools.
            </p>
            {editing.rateLimitPerCustomer.enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Max calls</label>
                  <input type="number" min="1" value={editing.rateLimitPerCustomer.maxCalls}
                    onChange={e => setEditing(p => ({ ...p, rateLimitPerCustomer: { ...p.rateLimitPerCustomer, maxCalls: Number(e.target.value) } }))}
                    className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Window (minutes)</label>
                  <input type="number" min="1" value={editing.rateLimitPerCustomer.windowMinutes}
                    onChange={e => setEditing(p => ({ ...p, rateLimitPerCustomer: { ...p.rateLimitPerCustomer, windowMinutes: Number(e.target.value) } }))}
                    className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
                </div>
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-[#D8DDE6]">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[12px] font-semibold text-[#706E6B] ">
                Response Fields {editing.resultMode === 'list' ? '(mapped once per record in the array)' : '(what to extract and return to the agent)'}
              </label>
              <button type="button" onClick={addField} className="flex items-center gap-1 px-2 py-1 bg-[#EAF4FE] text-[#0176D3] rounded text-[13px] font-semibold"><Plus size={12} /> Add Field</button>
            </div>
            <div className="space-y-2">
              {editing.responseFields.map((f, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <input type="text" value={f.label} onChange={e => patchField(idx, 'label', e.target.value)} placeholder="status" className="col-span-5 px-2 py-1.5 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
                  <input type="text" value={f.path} onChange={e => patchField(idx, 'path', e.target.value)} placeholder="data.tracking.status (dot-path in the JSON response)" className="col-span-6 px-2 py-1.5 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
                  <button type="button" onClick={() => removeField(idx)} className="col-span-1 p-1 text-[#BA0517] hover:bg-[#FDE7E9] rounded"><Trash2 size={13} /></button>
                </div>
              ))}
              {editing.responseFields.length === 0 && (
                <p className="text-[11px] text-[#706E6B] italic">No fields mapped — the agent will receive the raw response as-is.</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setEditing(null)} className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2]">Cancel</button>
            <button type="button" onClick={saveTool} disabled={saving} className="hover:bg-[#014486] flex items-center gap-1.5 px-4 py-2 bg-[#0176D3] text-white rounded whitespace-nowrap text-[13px] font-semibold disabled:opacity-60">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save Tool
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {tools.length === 0 && !editing && (
          <div className="sf-card p-10 text-center text-[#706E6B]">
            <Plug size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No API tools yet — create one above to give your agent live access to any external API.</p>
          </div>
        )}

        {tools.map(tool => (
          <div key={tool._id} className="sf-card overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <button type="button" onClick={() => openTestPanel(tool)} className="flex items-center gap-2 text-left flex-1">
                {expandedId === tool._id ? <ChevronDown size={16} className="text-[#706E6B]" /> : <ChevronRight size={16} className="text-[#706E6B]" />}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#080707] font-mono">{tool.name}</span>
                    <span className={cn('sf-badge', tool.isActive ? 'sf-badge-success' : 'sf-badge-neutral')}>{tool.isActive ? 'Active' : 'Disabled'}</span>
                    <span className="text-[10px] font-bold text-[#706E6B] uppercase">{tool.method}</span>
                  </div>
                  <p className="text-xs text-[#706E6B] mt-0.5">{tool.description}</p>
                </div>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => copyName(tool.name)} title="Copy tool name" className="p-1.5 text-[#706E6B] hover:bg-[#F3F2F2] rounded">
                  {copiedName === tool.name ? <Check size={14} className="text-[#2E844A]" /> : <Copy size={14} />}
                </button>
                <label className="flex items-center gap-1.5 text-[12px] font-semibold text-[#706E6B] mr-1">
                  <input type="checkbox" checked={tool.isActive} onChange={() => toggleActive(tool)} /> Enabled
                </label>
                <button type="button" onClick={() => setEditing({ ...BLANK_TOOL, ...tool, authValue: '' })} className="px-2 py-1 text-[13px] font-semibold text-[#0176D3] hover:bg-[#EAF4FE] rounded">Edit</button>
                <button type="button" onClick={() => deleteTool(tool._id)} className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded"><Trash2 size={14} /></button>
              </div>
            </div>

            {expandedId === tool._id && (
              <div className="border-t border-[#D8DDE6] bg-[#F9FAFB] p-4 space-y-3">
                <p className="text-xs font-bold text-[#706E6B] uppercase ">Test this tool</p>
                {tool.params.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {tool.params.map(p => (
                      <div key={p.name}>
                        <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">{p.name} {p.required && <span className="text-[#BA0517]">*</span>}</label>
                        <input
                          type="text"
                          value={testArgs[p.name] || ''}
                          onChange={e => setTestArgs(prev => ({ ...prev, [p.name]: e.target.value }))}
                          placeholder={p.description || p.type}
                          className="w-full px-2 py-1.5 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#706E6B] italic">This tool takes no parameters.</p>
                )}

                <button type="button" onClick={() => runTest(tool)} disabled={testing} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0176D3] text-white rounded text-[13px] font-semibold disabled:opacity-60">
                  {testing ? <RefreshCw size={13} className="animate-spin" /> : <Play size={13} />} Run
                </button>

                {testResult && (
                  <div className="space-y-2">
                    {testResult.error ? (
                      <p className="text-xs text-[#BA0517] font-medium">{testResult.error}</p>
                    ) : (
                      <>
                        <div>
                          <p className="text-[10px] font-bold text-[#706E6B] uppercase mb-1">
                            {editing?.resultMode === 'list' || testResult.records !== undefined
                              ? 'Mapped Records (first 10 — this is what an External Module page will show)'
                              : 'Mapped Result (what the agent will see)'}
                          </p>
                          <pre className="bg-[#080707] text-[#2E844A] text-[11px] p-3 rounded overflow-x-auto max-h-64">
                            {JSON.stringify(testResult.records !== undefined ? testResult.records : testResult.mapped, null, 2)}
                          </pre>
                          {testResult.records === null && (
                            <p className="text-[11px] text-[#DD7A01] mt-1">Array Path didn't resolve to a list — adjust it above and test again.</p>
                          )}
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-[#706E6B] uppercase mb-1">Raw Response (status {testResult.status})</p>
                          <pre className="bg-[#080707] text-[#706E6B] text-[11px] p-3 rounded overflow-x-auto max-h-48">{JSON.stringify(testResult.raw, null, 2)}</pre>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="pt-3 border-t border-[#D8DDE6] space-y-2">
                  <p className="text-xs font-bold text-[#706E6B] uppercase ">
                    Call Log <span className="font-normal normal-case">— every real invocation, from any channel</span>
                  </p>
                  {loadingLogs ? (
                    <Loader size={14} className="animate-spin text-[#706E6B]" />
                  ) : callLogs.length === 0 ? (
                    <p className="text-xs text-[#706E6B] italic">No calls recorded for this tool yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] text-[#706E6B] uppercase ">
                            <th className="text-left py-1 pr-3">Time</th>
                            <th className="text-left py-1 pr-3">Status</th>
                            <th className="text-left py-1 pr-3">Args</th>
                            <th className="text-left py-1 pr-3">Response / Error</th>
                            <th className="text-left py-1">ms</th>
                          </tr>
                        </thead>
                        <tbody>
                          {callLogs.map(log => {
                            const s = STATUS_LABELS[log.status] || { text: log.status, className: 'text-[#706E6B] bg-[#F3F2F2]' };
                            return (
                              <tr key={log._id} className="border-t border-[#EEF1F5]">
                                <td className="py-1.5 pr-3 whitespace-nowrap text-[#706E6B]">{new Date(log.createdAt).toLocaleString()}</td>
                                <td className="py-1.5 pr-3"><span className={cn('px-1.5 py-0.5 rounded font-semibold', s.className)}>{s.text}</span></td>
                                <td className="py-1.5 pr-3 font-mono max-w-[220px] truncate" title={JSON.stringify(log.args)}>{JSON.stringify(log.args)}</td>
                                <td className="py-1.5 pr-3 font-mono max-w-[280px] truncate" title={log.responseSummary}>{log.responseSummary}</td>
                                <td className="py-1.5">{log.durationMs}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
